import React, { useCallback, useMemo } from 'react';
import { Box } from '@material-ui/core';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { DataGrid, Typography as CommonsTypography } from '@orijinworks/frontend-commons';
import { downloadCsv } from '../../../../../v2/Form/utils/csvExport.util';
import {
  endOfDay,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import moment from 'moment';

const DATE_FMT = 'yyyy-MM-dd';

/** Date-range filter value from commons: "start,end" (single day duplicates end). */
function parseFilterRange(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const parts = value.split(',');
  const a = parts[0]?.trim();
  const b = (parts.length > 1 ? parts[1] : parts[0])?.trim();
  if (!a) {
    return null;
  }
  const fStart = startOfDay(parse(a, DATE_FMT, new Date()));
  const fEnd = endOfDay(parse(b || a, DATE_FMT, new Date()));
  if (!isValid(fStart) || !isValid(fEnd)) {
    return null;
  }
  return { fStart, fEnd };
}

function filterAssignmentStart(_columnName, value, data) {
  if (!value) {
    return data;
  }
  const range = parseFilterRange(value);
  if (!range) {
    return null;
  }
  const { fStart, fEnd } = range;
  const raw = data.rowData.startDate;
  if (typeof raw !== 'string' || raw === '—') {
    return null;
  }
  const d = startOfDay(parse(raw, DATE_FMT, new Date()));
  if (!isValid(d)) {
    return null;
  }
  return d >= fStart && d <= fEnd ? data : null;
}

function filterAssignmentEnd(_columnName, value, data) {
  if (!value) {
    return data;
  }
  const range = parseFilterRange(value);
  if (!range) {
    return null;
  }
  const { fStart, fEnd } = range;
  const endCell = data.rowData.endDate;
  const startCell = data.rowData.startDate;

  if (endCell === 'Ongoing' || !endCell || endCell === '—') {
    if (typeof startCell !== 'string' || startCell === '—') {
      return null;
    }
    const rowStart = startOfDay(parse(startCell, DATE_FMT, new Date()));
    if (!isValid(rowStart)) {
      return null;
    }
    return rowStart <= fEnd ? data : null;
  }

  const d = startOfDay(parse(String(endCell), DATE_FMT, new Date()));
  if (!isValid(d)) {
    return null;
  }
  return d >= fStart && d <= fEnd ? data : null;
}

const PLA_GRID_COLUMNS = [
  {
    name: 'locationName',
    displayName: 'Location',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'roleName',
    displayName: 'Role',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'startDate',
    displayName: 'Assignment start',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
    sortByProp: 'startDateSortValue',
    filter: {
      type: 'date-range',
      dateFormat: 'yyyy-MM-dd',
      monthsShown: 1,
      onCustomFilterCallback: filterAssignmentStart,
    },
  },
  {
    name: 'endDate',
    displayName: 'Assignment end',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
    sortByProp: 'endDateSortValue',
    filter: {
      type: 'date-range',
      dateFormat: 'yyyy-MM-dd',
      monthsShown: 1,
      onCustomFilterCallback: filterAssignmentEnd,
    },
  },
  {
    name: 'recorded',
    displayName: 'Recorded (system)',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
    sortByProp: 'recordedSortValue',
  },
  {
    name: 'status',
    displayName: 'Status',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
];

const PLA_STYLE_COLUMN_NAMES = PLA_GRID_COLUMNS.map((c) => c.name);
const PLA_EXPORT_COLUMN_KEYS = PLA_GRID_COLUMNS.map((c) => c.name);

function buildPlaRowStyles(isActive) {
  if (isActive) {
    const base = {
      backgroundColor: '#ffffff',
      color: 'var(--Greyscale---grey-120, #1A1A1A)',
    };
    return PLA_STYLE_COLUMN_NAMES.reduce((acc, name) => {
      if (name === 'locationName' || name === 'roleName') {
        acc[name] = { ...base, wordBreak: 'break-word' };
      } else {
        acc[name] = { ...base };
      }
      return acc;
    }, {});
  }
  const base = {
    backgroundColor: 'var(--Greyscale---grey-20, #F2F2F2)',
    color: 'var(--Greyscale---grey-90, #757575)',
  };
  return PLA_STYLE_COLUMN_NAMES.reduce((acc, name) => {
    if (name === 'locationName' || name === 'roleName') {
      acc[name] = { ...base, wordBreak: 'break-word' };
    } else {
      acc[name] = { ...base };
    }
    return acc;
  }, {});
}

function formatTs(s) {
  if (!s) {
    return '—';
  }
  const m = moment(s);
  return m.isValid() ? m.format('lll') : s;
}

function byRecencyForDisplay(a, b) {
  const aCreated = a.createdDate ? moment(a.createdDate) : null;
  const bCreated = b.createdDate ? moment(b.createdDate) : null;
  if (aCreated && bCreated && aCreated.isValid() && bCreated.isValid() && !aCreated.isSame(bCreated)) {
    return bCreated.valueOf() - aCreated.valueOf();
  }
  const aStart = a.startDate ? moment(a.startDate) : null;
  const bStart = b.startDate ? moment(b.startDate) : null;
  if (aStart && bStart && aStart.isValid() && bStart.isValid()) {
    return bStart.valueOf() - aStart.valueOf();
  }
  return (b.personLocationId || 0) - (a.personLocationId || 0);
}

/**
 * Builds a filesystem-safe segment: display name or first+last, lowercased,
 * non-alphanumerics → single underscores, plus _personKey_{id}.
 */
function buildStaffLocationHistoryExportBasename(exportContext) {
  if (!exportContext || typeof exportContext !== 'object') {
    return '';
  }
  const { displayName, firstName, lastName, personKey } = exportContext;
  const fromDisplay = displayName && String(displayName).trim();
  const fromParts = [firstName, lastName]
    .filter((p) => p != null && String(p).trim())
    .map((p) => String(p).trim())
    .join(' ');
  const rawName = fromDisplay || fromParts || '';
  let nameSlug = String(rawName)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')   // replace each char individually — no + quantifier
    .replace(/_+/g, '_')           // collapse runs of underscores (positive class, safe)
    .replace(/^_|_$/g, '');        // trim leading/trailing underscores
  nameSlug = nameSlug || 'staff';
  const keySegment = personKey != null && String(personKey).trim() !== ''
    ? `_${String(personKey).trim()}`
    : '';
  return `${nameSlug}${keySegment}`;
}

function StaffLocationHistoryGrid({ items, isLoading, classes, onRefresh, csvExportContext }) {
  const gridData = useMemo(() => {
    if (!items || !items.length) {
      return [];
    }
    return [...items].sort(byRecencyForDisplay).map((item) => {
      const startIso = item.startDate && moment(item.startDate).isValid()
        ? moment(item.startDate).format('YYYY-MM-DD')
        : '—';
      const endIso = item.endDate && moment(item.endDate).isValid()
        ? moment(item.endDate).format('YYYY-MM-DD')
        : null;
      const isActive = Boolean(item.active);
      const endDateDisplay = isActive ? 'Ongoing' : (endIso || '—');
      return {
        isSelected: false,
        rowData: {
          locationName: item.locationName || '—',
          roleName: item.roleDisplayName || item.roleName || '—',
          startDate: startIso,
          startDateSortValue: item.startDate || '0000-00-00',
          endDate: endDateDisplay,
          endDateSortValue: item.endDate || '9999-12-31',
          recorded: formatTs(item.createdDate),
          recordedSortValue: item.createdDate || 0,
          status: isActive ? 'Active' : 'Inactive',
        },
        styles: buildPlaRowStyles(isActive),
      };
    });
  }, [items]);

  const handleExportCsv = useCallback(() => {
    if (!gridData || gridData.length === 0) {
      return;
    }
    const headers = PLA_GRID_COLUMNS.map((c) => c.displayName);
    const rows = gridData.map((row) =>
      PLA_EXPORT_COLUMN_KEYS.map((key) => row.rowData[key]),
    );
    const userSegment = buildStaffLocationHistoryExportBasename(csvExportContext);
    const middle = userSegment ? `${userSegment}-` : '';
    downloadCsv({
      filename: `staff-facility-history-${middle}${moment().format('MM-DD-YYYY')}.csv`,
      headers,
      rows,
    });
  }, [gridData, csvExportContext]);

  const gridProps = {
    columns: PLA_GRID_COLUMNS,
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
    paginationMode: 'client',
    isAllSelected: false,
    showSelection: false,
    onSelectionChange: () => { },
    onRefreshHandler: typeof onRefresh === 'function' ? onRefresh : () => { },
    hasActionColumn: false,
    secondaryButtonProps: {
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportCsv,
      disabled: !gridData.length,
    },
    tableSx: {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
    },
  };

  if (isLoading) {
    return (
      <Box className={classes.plaHistoryGridWrap}>
        <DataGrid
          {...gridProps}
          data={[]}
          isLoading
        />
      </Box>
    );
  }

  if (!items || !items.length) {
    return (
      <CommonsTypography variant="body1" className={classes.plaHistoryEmpty}>
        No assignment history found.
      </CommonsTypography>
    );
  }

  return (
    <Box className={classes.plaHistoryGridWrap}>
      <DataGrid
        {...gridProps}
        data={gridData}
        isLoading={false}
      />
    </Box>
  );
}

export default StaffLocationHistoryGrid;
